import React from "react";
import { createColumnHelper } from "@tanstack/react-table";

import { PencilSimpleLineIcon, ChartBarIcon } from "@phosphor-icons/react";
import Icon from "components/Icon/Icon";

import CommonTooltip from "components/Tooltip/index";
import StatusPillButtons from "components/Buttons/StatusPillButtons";

import {
  CompanyNameCell,
  getFullName,
  renderValue
} from "utils/renderCompanyCell";
import ActionDropdown from "./components/ActionDropdown";
import { format } from "date-fns";
import { formatDate } from "./data";

const formatBoolean = (value) => {
  if (value === null || value === undefined) return "-";
  return value ? "Yes" : "No";
};

const columns = (
  navigate,
  searchParams,
  domainCountMap,
  handleStatusCountClick
) => {
  const columnHelper = createColumnHelper();

  return [
    columnHelper.accessor("id", {
      header: "ID",
      size: 80,
      headerClassName: "table-header mono",
      cellClassName: "table-cell mono",
      enableSorting: true,
      cell: (info) => renderValue(info.getValue())
    }),

    columnHelper.accessor((row) => row.name, {
      id: "name",
      header: "Name",
      size: 200,
      headerClassName: "table-header",
      cellClassName: "table-cell",
      enableSorting: true,
      cell: (info) => {
        const row = info.row.original;
        const fullName = renderValue(getFullName(row));
        return (
          <CompanyNameCell
            logo={row?.profile_image}
            name={fullName}
            title={fullName}
          />
        );
      }
    }),

    columnHelper.accessor("status", {
      header: "Account Status",
      size: 160,
      headerClassName: "table-header",
      cellClassName: "table-cell",
      enableSorting: false,
      cell: (info) => <StatusPillButtons status={info.getValue()} />
    }),

    columnHelper.accessor("company", {
      header: "Domains",
      size: 200,
      headerClassName: "table-header",
      cellClassName: "table-cell",
      enableSorting: false,
      cell: (info) => {
        const company = info.getValue();
        const domains = company?.domains?.map((d) => d.domain).join(", ");
        return renderValue(domains);
      }
    }),

    columnHelper.accessor("profile_image", {
      header: "Profile Image",
      size: 160,
      headerClassName: "table-header",
      cellClassName: "table-cell",
      enableSorting: false,
      cell: (info) => {
        const value = info.getValue();
        return value ? (
          <div className="table-link truncate-text">
            <a href={value} target="_blank" rel="noopener noreferrer">
              {value}
            </a>
          </div>
        ) : (
          "-"
        );
      }
    }),

    columnHelper.accessor("activated_count", {
      header: "Activated Count",
      size: 160,
      headerClassName: "table-header mono",
      cellClassName: "table-cell mono",
      enableSorting: false,

      cell: (info) => {
        const user = info.row.original;

        const companyId = user?.company?.id || user?.company_id;

        const count = domainCountMap?.[companyId]?.activated_count ?? 0;

        return count ? (
          <a
            href="#"
            onClick={(e) => {
              e.preventDefault();

              handleStatusCountClick({
                companyId,
                status: "activated"
              });
            }}
            className="status-count"
          >
            {count}
          </a>
        ) : (
          "0"
        );
      }
    }),

    columnHelper.accessor("deactivated_count", {
      header: "Deactivated Count",
      size: 180,
      headerClassName: "table-header mono",
      cellClassName: "table-cell mono",
      enableSorting: false,

      cell: (info) => {
        const user = info.row.original;

        const companyId = user?.company?.id || user?.company_id;

        const count = domainCountMap?.[companyId]?.deactivated_count ?? 0;

        return count ? (
          <a
            href="#"
            onClick={(e) => {
              e.preventDefault();

              handleStatusCountClick({
                companyId,
                status: "deactivated"
              });
            }}
            className="status-count"
          >
            {count}
          </a>
        ) : (
          "0"
        );
      }
    }),

    columnHelper.accessor("invite.invite_code", {
      header: "Invite Code",
      size: 160,
      headerClassName: "table-header mono",
      cellClassName: "table-cell mono",
      enableSorting: false,
      cell: (info) => renderValue(info.getValue())
    }),

    columnHelper.accessor("email", {
      header: "Email",
      size: 200,
      headerClassName: "table-header",
      enableSorting: false,
      cellClassName: "table-cell",
      cell: (info) => (
        <div className="truncate-text" title={renderValue(info.getValue())}>
          {renderValue(info.getValue())}
        </div>
      )
    }),

    columnHelper.accessor("email_verified_at", {
      header: "Email Verified At",
      size: 200,
      headerClassName: "table-header",
      enableSorting: true,
      cellClassName: "table-cell",
      cell: (info) => formatDate(info.getValue())
    }),

    columnHelper.accessor("phone_verified_at", {
      header: "Phone Verified At",
      size: 200,
      headerClassName: "table-header",
      enableSorting: true,
      cellClassName: "table-cell",
      cell: (info) => formatDate(info.getValue())
    }),

    columnHelper.accessor("linkedin_verified_at", {
      header: "LinkedIn Verified At",
      size: 200,
      headerClassName: "table-header",
      enableSorting: false,
      cellClassName: "table-cell",
      cell: (info) => formatDate(info.getValue())
    }),

    columnHelper.accessor("activated_at", {
      header: "Activated At",
      size: 240,
      headerClassName: "table-header",
      enableSorting: true,
      cellClassName: "table-cell",
      cell: (info) => formatDate(info.getValue())
    }),

    columnHelper.accessor("phone_number", {
      header: "Phone",
      size: 160,
      headerClassName: "table-header",
      cellClassName: "table-cell",
      enableSorting: false,
      cell: (info) => {
        const value = info.getValue();
        return value ? `+91 ${value}` : "-";
      }
    }),

    columnHelper.accessor("company_name", {
      header: "Company Name",
      size: 180,
      headerClassName: "table-header",
      enableSorting: false,
      cellClassName: "table-cell",
      cell: (info) => (
        <div className="truncate-text">{renderValue(info.getValue())}</div>
      )
    }),

    columnHelper.accessor("company_website", {
      header: "Company Website",
      size: 200,
      headerClassName: "table-header",
      enableSorting: false,
      cellClassName: "table-cell",
      cell: (info) => {
        const value = info.getValue();
        return value ? (
          <div className="table-link truncate-text">
            <a href={value} target="_blank" rel="noopener noreferrer">
              {value}
            </a>
          </div>
        ) : (
          "-"
        );
      }
    }),

    columnHelper.accessor("designation", {
      header: "Designation",
      size: 180,
      headerClassName: "table-header",
      enableSorting: false,
      cellClassName: "table-cell",
      cell: (info) => (
        <div className="truncate-text">{renderValue(info.getValue())}</div>
      )
    }),

    columnHelper.accessor("total_work_experience", {
      header: "Work Experience",
      size: 160,
      headerClassName: "table-header",
      enableSorting: false,
      cellClassName: "table-cell",
      cell: (info) => renderValue(info.getValue())
    }),

    columnHelper.accessor("is_super_admin", {
      header: "Super Admin",
      size: 160,
      headerClassName: "table-header",
      enableSorting: false,
      cellClassName: "table-cell",
      cell: (info) => (
        <StatusPillButtons status={formatBoolean(info.getValue())} />
      )
    }),

    columnHelper.accessor("brands_worked_with", {
      header: "Brands Worked With",
      size: 200,
      headerClassName: "table-header",
      enableSorting: false,
      cellClassName: "table-cell",
      cell: (info) => (
        <div className="truncate-text">{renderValue(info.getValue())}</div>
      )
    }),

    columnHelper.accessor("profile_completion_percentage", {
      header: "Profile Complete %",
      size: 180,
      headerClassName: "table-header mono",
      cellClassName: "table-cell mono",
      enableSorting: false,
      cell: (info) => {
        const value = info.getValue();
        return value !== null && value !== undefined ? `${value}%` : "-";
      }
    }),

    columnHelper.accessor("approval_type", {
      header: "Approval Type",
      size: 180,
      headerClassName: "table-header",
      enableSorting: false,
      cellClassName: "table-cell",
      cell: (info) => renderValue(info.getValue())
    }),

    columnHelper.accessor("approval_type_updated", {
      header: "Approval Type Updated",
      size: 180,
      headerClassName: "table-header mono",
      cellClassName: "table-cell mono",
      enableSorting: false,
      cell: (info) => renderValue(info.getValue())
    }),

    columnHelper.accessor("signup_after_verified", {
      header: "Signup After Verified",
      size: 180,
      headerClassName: "table-header",
      enableSorting: false,
      cellClassName: "table-cell",
      cell: (info) => (
        <StatusPillButtons status={formatBoolean(info.getValue())} />
      )
    }),

    columnHelper.accessor("level", {
      header: "Level",
      size: 140,
      headerClassName: "table-header",
      enableSorting: false,
      cellClassName: "table-cell",
      cell: (info) => (
        <StatusPillButtons status={renderValue(info.getValue())} />
      )
    }),

    columnHelper.accessor("updated_at", {
      header: "Updated At",
      size: 200,
      headerClassName: "table-header",
      enableSorting: false,
      cellClassName: "table-cell",
      cell: (info) => formatDate(info.getValue())
    }),

    columnHelper.accessor("goal", {
      header: "Goal",
      size: 160,
      headerClassName: "table-header",
      enableSorting: false,
      cellClassName: "table-cell",
      cell: (info) => renderValue(info.getValue())
    }),

    columnHelper.accessor("linkedin_url", {
      header: "LinkedIn URL",
      size: 200,
      headerClassName: "table-header",
      enableSorting: false,
      cellClassName: "table-cell",
      cell: (info) => {
        const value = info.getValue();
        return value ? (
          <div className="table-link truncate-text">
            <a href={value} target="_blank" rel="noopener noreferrer">
              {value}
            </a>
          </div>
        ) : (
          "-"
        );
      }
    }),

    columnHelper.accessor("last_active_at", {
      header: "Last Active At",
      size: 200,
      headerClassName: "table-header",
      enableSorting: false,
      cellClassName: "table-cell",
      cell: (info) => formatDate(info.getValue())
    }),

    columnHelper.accessor("email_updated", {
      header: "Email Updated",
      size: 160,
      headerClassName: "table-header",
      enableSorting: false,
      cellClassName: "table-cell",
      cell: (info) => (
        <StatusPillButtons status={formatBoolean(info.getValue())} />
      )
    }),

    columnHelper.accessor("cmpy_working_status_updated_at", {
      header: "Company Status Updated At",
      size: 220,
      headerClassName: "table-header",
      enableSorting: false,
      cellClassName: "table-cell",
      cell: (info) => formatDate(info.getValue())
    }),

    columnHelper.accessor("referral_source", {
      header: "Referral Source",
      size: 160,
      headerClassName: "table-header",
      enableSorting: false,
      cellClassName: "table-cell",
      cell: (info) => renderValue(info.getValue())
    }),

    columnHelper.accessor("account_type", {
      header: "Account Type",
      size: 160,
      headerClassName: "table-header",
      enableSorting: false,
      cellClassName: "table-cell",
      cell: (info) => renderValue(info.getValue())
    }),

    columnHelper.accessor("source", {
      header: "Source",
      size: 200,
      headerClassName: "table-header",
      enableSorting: false,
      cellClassName: "table-cell",
      cell: (info) => (
        <StatusPillButtons status={renderValue(info.getValue())} />
      )
    }),

    columnHelper.accessor("phone_number_changed_at", {
      header: "Phone Changed At",
      size: 200,
      headerClassName: "table-header",
      enableSorting: false,
      cellClassName: "table-cell",
      cell: (info) => formatDate(info.getValue())
    }),

    columnHelper.accessor("deactivated_at", {
      header: "Deactivated At",
      size: 200,
      headerClassName: "table-header",
      enableSorting: false,
      cellClassName: "table-cell",
      cell: (info) => formatDate(info.getValue())
    }),

    columnHelper.accessor("deactivated_reason", {
      header: "Deactivated Reason",
      size: 200,
      headerClassName: "table-header",
      enableSorting: false,
      cellClassName: "table-cell",
      cell: (info) => (
        <div className="truncate-text">{renderValue(info.getValue())}</div>
      )
    }),

    columnHelper.accessor("type", {
      header: "Type",
      size: 160,
      headerClassName: "table-header",
      enableSorting: false,
      cellClassName: "table-cell",
      cell: (info) => renderValue(info.getValue())
    }),

    columnHelper.accessor("updated_to_new_theme", {
      header: "New Theme",
      size: 160,
      headerClassName: "table-header",
      enableSorting: false,
      cellClassName: "table-cell",
      cell: (info) => (
        <StatusPillButtons status={formatBoolean(info.getValue())} />
      )
    }),

    columnHelper.accessor("theme_change_popup_shown_at", {
      header: "Theme Popup At",
      size: 200,
      headerClassName: "table-header",
      enableSorting: false,
      cellClassName: "table-cell",
      cell: (info) => formatDate(info.getValue())
    }),

    columnHelper.accessor("is_company_changed", {
      header: "Company Changed",
      size: 160,
      headerClassName: "table-header",
      enableSorting: false,
      cellClassName: "table-cell",
      cell: (info) => (
        <StatusPillButtons status={formatBoolean(info.getValue())} />
      )
    }),

    columnHelper.accessor("work_persona", {
      header: "Work Persona",
      size: 160,
      headerClassName: "table-header",
      enableSorting: false,
      cellClassName: "table-cell",
      cell: (info) => renderValue(info.getValue())
    }),

    columnHelper.accessor("activated_popup_shown_at", {
      header: "Activated Popup At",
      size: 200,
      headerClassName: "table-header",
      enableSorting: false,
      cellClassName: "table-cell",
      cell: (info) => formatDate(info.getValue())
    }),

    columnHelper.accessor("cohort", {
      header: "Cohort",
      size: 160,
      headerClassName: "table-header",
      enableSorting: false,
      cellClassName: "table-cell",
      cell: (info) => renderValue(info.getValue())
    }),

    columnHelper.accessor("invite_code_entry_deadline", {
      header: "Invite Deadline",
      size: 200,
      headerClassName: "table-header",
      enableSorting: false,
      cellClassName: "table-cell",
      cell: (info) => formatDate(info.getValue())
    }),

    columnHelper.accessor("is_endorse_claimed", {
      header: "Endorse Claimed",
      size: 180,
      headerClassName: "table-header",
      enableSorting: false,
      cellClassName: "table-cell",
      cell: (info) => (
        <StatusPillButtons status={formatBoolean(info.getValue())} />
      )
    }),

    columnHelper.accessor("endorse_claimed_at", {
      header: "Endorse Claimed At",
      size: 200,
      headerClassName: "table-header",
      enableSorting: false,
      cellClassName: "table-cell",
      cell: (info) => formatDate(info.getValue())
    }),

    columnHelper.accessor("last_endorse_popup_shown_at", {
      header: "Last Endorse Popup At",
      size: 200,
      headerClassName: "table-header",
      enableSorting: false,
      cellClassName: "table-cell",
      cell: (info) => formatDate(info.getValue())
    }),

    columnHelper.accessor("show_first_time_wave_popup", {
      header: "Show Wave Popup",
      size: 160,
      headerClassName: "table-header",
      enableSorting: false,
      cellClassName: "table-cell",
      cell: (info) => (
        <StatusPillButtons status={formatBoolean(info.getValue())} />
      )
    }),

    columnHelper.accessor("id_hash", {
      header: "Id Hash",
      size: 200,
      headerClassName: "table-header mono",
      cellClassName: "table-cell mono",
      enableSorting: false,
      cell: (info) => (
        <div className="truncate-text">{renderValue(info.getValue())}</div>
      )
    }),

    columnHelper.accessor("show_wave_toast", {
      header: "Show Wave Toast",
      size: 160,
      headerClassName: "table-header",
      enableSorting: false,
      cellClassName: "table-cell",
      cell: (info) => (
        <StatusPillButtons status={formatBoolean(info.getValue())} />
      )
    }),

    columnHelper.accessor("created_at", {
      header: "Created At",
      size: 207,
      headerClassName: "table-header",
      enableSorting: false,
      cellClassName: "table-cell",
      cell: (info) => formatDate(info.getValue())
    }),

    columnHelper.display({
      id: "actions",
      header: "Actions",
      size: 140,
      cellClassName: "actions",
      enableSorting: false,
      cell: (info) => {
        const userId = info.row.original.id;
        return (
          <div className="hover-btn-wrapper">
            <CommonTooltip theme="dark" tooltipContent="View User Insights">
              <div className="hover-actions">
                <Icon
                  component={ChartBarIcon}
                  weight="regular"
                  onClick={() =>
                    navigate(
                      `/marketer-users/user-insights/${userId}?${searchParams.toString()}`
                    )
                  }
                />
              </div>
            </CommonTooltip>
            <CommonTooltip theme="dark" tooltipContent="Edit Details">
              <div className="hover-actions">
                <Icon
                  component={PencilSimpleLineIcon}
                  weight="regular"
                  onClick={() =>
                    navigate(
                      `/marketer-users/${userId}?mode=view&${searchParams.toString()}`
                    )
                  }
                />
              </div>
            </CommonTooltip>
            <ActionDropdown userInfo={info.row.original} />
          </div>
        );
      }
    })
  ];
};

export default columns;
